// Entry: auto-init on [data-dc-cake-editor]; without one, mount at the end of the buy area
// with paper and product id read off the script tag; window.DreamcakesCakeEditor.init() for GTM.

import { createRoot } from 'react-dom/client';
import { SCRIPT_ORIGIN } from './assets';
import { App } from './App';
import { PAPERS, type PaperId } from './design';
import { STRINGS, type Strings } from './strings';
import { injectFontFaces } from './text';
import { STYLES } from './styles';

// Only valid while this script executes, so capture it at module load, not in a callback.
const SCRIPT_EL = document.currentScript as HTMLScriptElement | null;
// WGR's product template: the quantity + buy button block.
const DEFAULT_MOUNT_AREA = '.js-add-to-cart-area, #js-add-to-cart-area';

export interface InitOptions {
  mountEl: HTMLElement;
  paper: PaperId;
  productId: string;
  orientationToggle?: boolean;
  apiBase?: string;
  targetInputSelector?: string;
  addToCartSelector?: string;
  targetDpi?: number;
  minDpi?: number;
  /** Open an existing design instead of whatever this browser last saved for the product.
   *  The reference is the only capability needed to read one, so an admin page can hand it in. */
  reference?: string;
  onDesignSaved?(reference: string, previewUrl: string): void;
  labels?: Partial<Strings>;
}

/** InitOptions with every default applied. */
export interface ResolvedOptions {
  mountEl: HTMLElement;
  paper: PaperId;
  productId: string;
  orientationToggle: boolean;
  apiBase: string;
  targetInputSelector?: string;
  addToCartSelector?: string;
  targetDpi: number;
  minDpi: number;
  reference?: string;
  onDesignSaved?: InitOptions['onDesignSaved'];
  labels: Strings;
}

export function init(o: InitOptions): void {
  if (!(o.paper in PAPERS)) throw new Error(`unknown paper ${o.paper}`);
  if (o.mountEl.dataset.dcInit) return; // double init guard
  o.mountEl.dataset.dcInit = '1';
  const opts: ResolvedOptions = {
    mountEl: o.mountEl,
    paper: o.paper,
    productId: o.productId,
    orientationToggle: o.orientationToggle ?? true,
    apiBase: (o.apiBase ?? SCRIPT_ORIGIN).replace(/\/$/, ''),
    targetInputSelector: o.targetInputSelector,
    addToCartSelector: o.addToCartSelector,
    targetDpi: o.targetDpi ?? 300,
    // Frosting sheets and edible ink soften everything; 100 dpi is where it starts to look bad.
    minDpi: o.minDpi ?? 100,
    reference: o.reference,
    onDesignSaved: o.onDesignSaved,
    labels: { ...STRINGS, ...o.labels },
  };
  injectFontFaces();
  const shadow = o.mountEl.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = STYLES;
  shadow.appendChild(style);
  const root = document.createElement('div');
  shadow.appendChild(root);
  createRoot(root).render(<App opts={opts} mountEl={o.mountEl} />);
}

/**
 * No <div data-dc-cake-editor> in the template: create one as the last child of the buy area.
 * Every data-* on the script tag is copied onto it, so `data-paper`, `data-product-id` and the
 * optional overrides work exactly as on the div. `data-mount` picks another area.
 */
function autoMount(): void {
  const { mount, ...attrs } = SCRIPT_EL?.dataset ?? {};
  if (!attrs.paper || !attrs.productId) return;
  const area = document.querySelector<HTMLElement>(mount || DEFAULT_MOUNT_AREA);
  if (!area) return console.warn('dc-cake-editor: no mount area found for', mount || DEFAULT_MOUNT_AREA);
  const el = document.createElement('div');
  Object.assign(el.dataset, attrs, { dcCakeEditor: '' });
  area.appendChild(el);
}

function autoInit(): void {
  if (!document.querySelector('[data-dc-cake-editor]')) autoMount();
  document.querySelectorAll<HTMLElement>('[data-dc-cake-editor]').forEach((el) => {
    const { paper, productId, apiBase, targetInput, addToCart, minDpi, reference } = el.dataset;
    if (!paper || !productId) return console.warn('dc-cake-editor: data-paper and data-product-id are required', el);
    init({ mountEl: el, paper: paper as PaperId, productId, apiBase, targetInputSelector: targetInput, addToCartSelector: addToCart,
      minDpi: minDpi ? Number(minDpi) : undefined, reference });
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
else autoInit();
